import axios from 'axios';
import Store from '../../../Store/Store';
import Global from '../../../Services/Common/Global';
import Commons from '../../../Services/Common/Common';
import { TbmActions } from '../../../Reducer/TbmReducer';

/**
 * TBM 게스트(일반 카메라 QR 웹 참석) API
 * - 회원 토큰(BaseInfo.userInfo.token) 대신 게스트 로그인 토큰을 사용
 * - 게스트 토큰이 거부되면(서명 완료·강퇴·TBM 삭제·만료) status를 expired로 바꿔 종료 화면으로 전환
 */
export const GetTbmGuest = () => Store.getState()?.tbm?.guest ?? null;

/** 게스트로 TBM 진행 중인지 (TBM 공통 화면에서 회원/게스트 API 분기용) */
export const IsTbmGuestMode = () => {
  const guest = GetTbmGuest();
  return Boolean(guest?.accessToken) && guest?.status === 'active';
};

const GuestPost = async (
  path,
  { params, body = null, responseType, anonymous = false } = {}
) => {
  const headers = { 'Content-Type': 'application/json' };
  if (!anonymous) {
    headers.Authorization = `Bearer ${GetTbmGuest()?.accessToken ?? ''}`;
  }

  try {
    return await axios.post(`${Global.base.useUrl}${path}`, body, {
      headers,
      params,
      responseType,
    });
  } catch (error) {
    const status = error?.response?.status;
    if (!anonymous && (status === 401 || status === 403)) {
      Store.dispatch(TbmActions.setGuestStatus('expired'));
      // useErrorHandler가 'Request failed…' 대신 서버 안내 문구를 보여주도록
      error.errorInfo = {
        type: 'GUEST_EXPIRED',
        userMessage:
          error?.response?.data?.message || 'TBM 참석이 종료되었습니다.',
      };
    }
    throw error;
  }
};

const urls = Global.urls.TbmGuest;

/** QR 입장 토큰으로 TBM 안내 조회 (비로그인) */
export const GetGuestSessionInfo = async (joinToken) =>
  (
    await GuestPost(urls.getTbmGuestR1, {
      params: { JoinToken: joinToken },
      anonymous: true,
    })
  ).data;

/** 게스트 입장 (비로그인) */
export const JoinGuest = async (payload) =>
  (await GuestPost(urls.saveTbmGuestU1, { body: payload, anonymous: true }))
    .data;

/** TBM 세션 1건 (getTbmNewR5와 같은 형식) */
export const GetGuestSession = async () =>
  (await GuestPost(urls.getTbmGuestR2)).data;

/** TBM 단계 (getTbmNewR3와 같은 형식) */
export const GetGuestSteps = async () =>
  (await GuestPost(urls.getTbmGuestR3)).data;

/** TBM 콘텐츠 (getTbmNewR10과 같은 형식) */
export const GetGuestContents = async () =>
  (await GuestPost(urls.getTbmGuestR4)).data;

/** 작업허가서 목록 (getSamBasic02R4와 같은 형식, 조회 조건은 서버가 세션 기준으로 정함) */
export const GetGuestPermits = async () =>
  (await GuestPost(urls.getTbmGuestR5)).data;

/** 작업허가서 상세 (getSamBasic02R5와 같은 형식) */
export const GetGuestPermitDetail = async (pmCd) =>
  (
    await GuestPost(urls.getTbmGuestR6, {
      params: { PmCd: Commons.EncryptAES(String(pmCd ?? '')) },
    })
  ).data;

/** 작업허가서 PDF — Commons.Axios('report')와 같이 axios 응답(data: Blob)을 그대로 반환 */
export const GetGuestPermitReport = async (printData) =>
  GuestPost(urls.getTbmGuestReport, { body: printData, responseType: 'blob' });

/** 최종 서명 저장 — 외부 참석자 소속·성명 포함 */
export const SaveGuestSign = async (signImagePath, { depNm, name } = {}) =>
  (
    await GuestPost(urls.saveTbmGuestU2, {
      body: {
        SignImagePath: Commons.EncryptAES(String(signImagePath ?? '')),
        DepNm: Commons.EncryptAES(String(depNm ?? '').trim()),
        Name: Commons.EncryptAES(String(name ?? '').trim()),
      },
    })
  ).data;
